import { History } from "history";
import React, {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Dispatch, useEffect, useReducer } from "react";
import { useHistory } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { ViImageData, ViCoreProcessing } from "../models";
import { ViError, ViErrorType } from "../models/Errors";
import { ViInferenceClient } from "../services/";
import { AppContext, useApp } from "./AppContext";
import { useVi, ViAction, ViActionType } from "./ViContext";

interface ViInferenceState {
  isViInference: boolean;
  status: ViInferenceActionType | "READY";
  isLoading: boolean;
}

interface ViInferenceContext extends ViInferenceState {
  dispatch: Dispatch<ViInferenceAction>;
}

enum ViInferenceActionType {
  GetProcessing = "GET PROCESSING",
  GetProcessingRequested = "GET PROCESSING - REQUESTED",
  GetProcessingSuccess = "GET PROCESSING - SUCCESS",
  Error = "Error",
}

type ViInferenceAction =
  | { type: ViInferenceActionType.GetProcessing; payload: { images: ViImageData[] } }
  | { type: ViInferenceActionType.GetProcessingRequested }
  | {
      type: ViInferenceActionType.GetProcessingSuccess;
      payload: { processingResponse: ViCoreProcessing };
    }
  | { type: ViInferenceActionType.Error; payload: { error: ViError } };

const initialState = {
  status: "READY" as const,
  isViInference: true,
  isLoading: false,
};

const ViInferenceSubContext = createContext<ViInferenceContext | undefined>(undefined);
export const useViInference = () => {
  const context = useContext(ViInferenceSubContext);
  if (context === undefined)
    throw new ViError(
      "useViInference must be used within a ViInferenceSubContext Provider"
    );
  return context;
};

export const ViInferenceProvider = React.memo(
  ({ children, baseURL }: { children: ReactElement; baseURL: string }) => {
    const App = useApp();
    const history = useHistory();
    const { actions: viActions, dispatch: viDispatch } = useVi();
    const [actions, setActions] = useState<ViInferenceAction[]>([]);
    const [state, rawDispatch] = useReducer(reducer, initialState);
    const viInferenceClient = useMemo(() => ViInferenceClient.init(baseURL), [baseURL]);

    const dispatch = useCallback((action: ViInferenceAction) => {
      rawDispatch(action);
      setActions((actions) => [...actions, action]);
    }, []);

    useEffect(() => {
      for (const action of actions) {
        sideEffects(state, action, {
          dispatch,
          viDispatch,
          history,
          viInferenceClient,
          App,
        });
      }
      if (actions.length) setActions([]);
    }, [actions, history, dispatch, viDispatch, state, App, viInferenceClient]);

    useEffect(() => {
      for (const action of viActions) {
        rawDispatch(action);
        viSideEffects(state, action, { history, dispatch, viDispatch });
      }
    }, [viActions, state, history, dispatch, viDispatch]);

    return (
      <ViInferenceSubContext.Provider value={{ ...state, dispatch }}>
        {children}
      </ViInferenceSubContext.Provider>
    );
  }
);

// export const init = (baseURL: string) => {
//   const viInferenceClient = ViInferenceClient.init(baseURL);
//   return function ViInferenceSubContext(
//     viActions: ViAction[],
//     viDispatch: Dispatch<ViAction>
//   ) {
//     const App = useApp();
//     const history = useHistory();
//     const [state, rawDispatch] = useReducer(reducer, initialState);
//     const [actions, setActions] = useState<ViInferenceAction[]>([]);

//     const dispatch = useCallback((action: ViInferenceAction) => {
//       rawDispatch(action);
//       setActions((actions) => [...actions, action]);
//     }, []);

//     useEffect(() => {
//       for (const action of actions) {
//         sideEffects(state, action, {
//           dispatch,
//           viDispatch,
//           history,
//           viInferenceClient,
//           App,
//         });
//       }
//       if (actions.length) setActions([]);
//     }, [actions, history, dispatch, viDispatch, state, App]);

//     useEffect(() => viActions.forEach((action) => rawDispatch(action)), [viActions]);

//     useEffect(() => {
//       for (const action of viActions) {
//         viSideEffects(state, action, { history, dispatch, viDispatch });
//       }
//     }, [viActions, state, history, dispatch, viDispatch]);
//   };
// };

const viSideEffects = (
  state: ViInferenceState,
  action: ViAction,
  {
    history,
    dispatch,
    viDispatch,
  }: {
    history: History;
    dispatch: Dispatch<ViInferenceAction>;
    viDispatch: Dispatch<ViAction>;
  }
) => {
  try {
    switch (action.type) {
      case ViActionType.ImagesLoaded: {
        dispatch({
          type: ViInferenceActionType.GetProcessing,
          payload: action.payload,
        });
        break;
      }

      case ViActionType.ImageDeleted: {
        const { newImages } = action.payload;
        if (newImages?.length)
          dispatch({
            type: ViInferenceActionType.GetProcessing,
            payload: { images: newImages },
          });
        break;
      }
    }
  } catch (e) {
    const error: ViError = { ...e, actionType: action.type };
    dispatch({ type: ViInferenceActionType.Error, payload: { error } });
    viDispatch({ type: ViActionType.Error, payload: { error } });
  }
};

const sideEffects = async (
  state: ViInferenceState,
  action: ViInferenceAction,
  {
    history,
    dispatch,
    viDispatch,
    viInferenceClient,
    App,
  }: {
    history: History;
    dispatch: Dispatch<ViInferenceAction>;
    viDispatch: Dispatch<ViAction>;
    viInferenceClient: typeof ViInferenceClient;
    App: AppContext;
  }
) => {
  try {
    switch (action.type) {
      case ViInferenceActionType.GetProcessing: {
        const uploadedImages = action.payload.images;
        const t0 = performance.now();
        const getProcessingPromise = viInferenceClient.getProcessing(uploadedImages);
        dispatch({
          type: ViInferenceActionType.GetProcessingRequested,
        });
        const getProcessingResponse = await getProcessingPromise;
        const t1 = performance.now();
        const timing = Math.round((t1 - t0) / 100) / 10;
        const {
          caseLevel: { aggregatedPartDamages, ...caseLevel },
          imageLevel: images,
        } = getProcessingResponse;
        history.replace("/vi/inference");
        const payload = {
          processingResponse: {
            ...caseLevel,
            aggregatedPartDamages: aggregatedPartDamages.map((aPD) => ({
              id: uuidv4(),
              ...aPD,
            })),
            images: images.map(({ imageId, ...img }) => ({
              id: imageId,
              ...img,
            })),
            timing,
          },
        };
        dispatch({
          type: ViInferenceActionType.GetProcessingSuccess,
          payload,
        });
        viDispatch({
          type: ViActionType.ViResultsReceived,
          payload,
        });
        break;
      }

      default:
        break;
    }
  } catch (e) {
    const error = new ViError(e);
    error.actionType = action.type;
    App.handleError && App.handleError(error);
    if (error.type !== ViErrorType.Warning && error.type !== ViErrorType.Silent) {
      dispatch({
        type: ViInferenceActionType.Error,
        payload: { error },
      });
    }
    viDispatch({ type: ViActionType.Error, payload: { error } });
    return false;
  }
};

export const reducer = (
  state: ViInferenceState,
  action: ViAction | ViInferenceAction
): ViInferenceState => {
  switch (action.type) {
    case ViInferenceActionType.GetProcessing:
      return { ...state, status: action.type, isLoading: true };

    case ViInferenceActionType.GetProcessingRequested:
      return { ...state, status: action.type };

    case ViInferenceActionType.GetProcessingSuccess:
      return { ...state, status: "READY" as const, isLoading: false };

    case ViInferenceActionType.Error:
      return initialState;

    default:
      return state;
  }
};
